import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import Datatable from "../../../../core/common/dataTable";
import { ColumnsType } from "antd/es/table";
import { getSearchableHeader } from "../../../utils/getSearchableHeader";
import Footer from "../../../../core/common/Footer";
import useApi from "../../../../core/customhook/useApi";
import { useAuth } from "../../../../core/context/useAuthContext";
import SnipperLoader from "../../../../core/common/loader/SnipperLoader";
import Swal from "sweetalert2";
import { displayValue } from "../../../../core/common/TabelValue";
import { cellWidth } from "../../../../core/methods/tableCellWidth";

const LOBList = () => {
  const navigate = useNavigate();
  const [showGuide, setShowGuide] = useState<boolean>(true);
  const [departmentList, setDepartmentList] = useState<any[]>([]);
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});

  const [pagination, setPagination] = useState({ page: 1, limit: 1000 });
  const [total, setTotal] = useState<number>(0);

  const { auth } = useAuth();
  const orgId = auth?.orgId;

  const getDepartmentReport = useApi<any>(
    `/api/v1/master/organizations/${orgId}/departments-report?page=${pagination.page}&limit=${pagination.limit}`,
    {
      method: "GET",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  const updateLoBStatus = useApi<any>("", {
    method: "PATCH",
    autoCall: false,
    handleGlobalError: false,
  });

  const lobListLoading = getDepartmentReport.loading;

  const getDepartmentList = async () => {
    if (!orgId) return;

    try {
      const res = await getDepartmentReport.callApi();
      if (res?.success) {
        const data = res?.data;

        const rows = Array.isArray(data)
          ? data
          : (data?.rows ?? data?.table ?? []);
        setDepartmentList(rows);

        const pg = data?.pagination;
        setTotal(
          pg?.total ??
            pg?.totalRecords ??
            (Array.isArray(data) ? data.length : 0),
        );
      } else {
        setDepartmentList([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Failed to fetch departments:", error);
      setDepartmentList([]);
      setTotal(0);
    }
  };

  useEffect(() => {
    getDepartmentList();
  }, [orgId, pagination.page, pagination.limit]);

  const filteredData = useMemo(() => {
    return departmentList.filter((row) =>
      Object.entries(columnSearch).every(([key, value]) =>
        value
          ? row[key as keyof typeof row]
              ?.toString()
              .toLowerCase()
              .includes(value.toLowerCase())
          : true,
      ),
    );
  }, [departmentList, columnSearch]);

  // ---- Toggle Status ----
  const handleStatusToggle = async (record: any) => {
    const previousStatus = record.department_status;
    setDepartmentList((prev: any[]) =>
      prev.map((item) =>
        item.department_id === record.department_id
          ? { ...item, department_status: !previousStatus }
          : item,
      ),
    );

    try {
      const payload = { is_active: !previousStatus };

      const res = await updateLoBStatus.callApi(
        payload,
        `/api/v1/master/departments/${record.department_id}/status`,
      );

      if (!res?.success) {
        throw new Error(res?.message);
      }
    } catch (error: any) {
      setDepartmentList((prev: any[]) =>
        prev.map((item) =>
          item.department_id === record.department_id
            ? { ...item, department_status: previousStatus }
            : item,
        ),
      );

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to update department status.",
      });
    }
  };

  // ---- Table change (page / page size) ----
  const handleTableChange = (pgConfig: any) => {
    const newPage = pgConfig.current;
    const newLimit = pgConfig.pageSize;

    if (newPage === pagination.page && newLimit === pagination.limit) return;

    setPagination({
      // page size badalne par first page par jao
      page: newLimit !== pagination.limit ? 1 : newPage,
      limit: newLimit,
    });
  };

  // ---- Columns ----
  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "s_no",
      width: 80,
      align: "center",
      render: (_: any, __: any, index: number) =>
        (pagination.page - 1) * pagination.limit + index + 1,
    },
    {
      title: getSearchableHeader(
        "Department Code",
        "department_code",
        setColumnSearch,
      ),
      dataIndex: "department_code",
      key: "department_code",
      sorter: (a, b) =>
        (a.department_code ?? "").localeCompare(b.department_code ?? ""),
      render: displayValue,
    },
    {
      title: getSearchableHeader(
        "Department",
        "department_name",
        setColumnSearch,
      ),
      dataIndex: "department_name",
      key: "department_name",
      sorter: (a, b) =>
        (a.department_name ?? "").localeCompare(b.department_name ?? ""),
      render: displayValue,
    },
    {
      title: getSearchableHeader("Program", "program_name", setColumnSearch),
      dataIndex: "program_name",
      key: "program_name",
      sorter: (a, b) =>
        (a.program_name ?? "").localeCompare(b.program_name ?? ""),
      render: displayValue,
      onCell: () => cellWidth(300),
    },
    {
      title: getSearchableHeader(
        "Program Location",
        "program_location",
        setColumnSearch,
      ),
      dataIndex: "program_location",
      key: "program_location",
      sorter: (a, b) =>
        (a.program_location ?? "").localeCompare(b.program_location ?? ""),
      render: displayValue,
    },
    {
      title: getSearchableHeader("Project", "project_name", setColumnSearch),
      dataIndex: "project_name",
      key: "project_name",
      sorter: (a, b) =>
        (a.project_name ?? "").localeCompare(b.project_name ?? ""),
      render: displayValue,
      onCell: () => cellWidth(200),
    },
    {
      title: "Active/Inactive",
      dataIndex: "department_status",
      key: "department_status",
      align: "center",
      render: (_: any, record: any) => (
        <div className="form-check form-switch d-flex justify-content-center">
          <input
            className="form-check-input"
            type="checkbox"
            checked={!!record.department_status}
            onChange={() => handleStatusToggle(record)}
          />
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_: any, record: any) => (
        <button
          className="btn btn-sm btn-primary"
          onClick={() =>
            navigate(all_routes.updateLob, {
              state: { departmentId: record.department_id },
            })
          }
        >
          <i className="ti ti-edit"></i>
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Line of Business</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Account Activation</li>
                <li className="breadcrumb-item">Account Configuration</li>
                <li className="breadcrumb-item active">Line of Business</li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 align-items-center mt-3">
          <button
            className="btn btn-primary"
            onClick={() => navigate(all_routes.program)}
          >
            <i className="ti ti-plus"></i> Add Line of Business
          </button>
          <button
            className="btn btn-primary"
            onClick={() => navigate(all_routes.addBulkLob)}
          >
            <i className="ti ti-plus"></i> Add Hierarchy
          </button>
          <button
            className="btn border bg-white px-3"
            onClick={() => setShowGuide((prev) => !prev)}
          >
            <i className="ti ti-info-circle-filled fs-16"></i> Guide
          </button>
        </div>

        {showGuide && (
          <div
            style={{
              overflow: "hidden",
              maxHeight: showGuide ? "500px" : "0px",
              transition: "all 0.4s ease",
            }}
          >
            {/* Top Arrow */}
            <div className="text-end pe-3">
              <i
                className="ti ti-triangle-inverted-filled"
                style={{
                  fontSize: "22px",
                  color: "#cfcfcf",
                  lineHeight: 0,
                }}
              ></i>
            </div>

            {/* Guide Box */}
            <div
              className="card border-0 shadow-sm"
              style={{ borderRadius: "4px" }}
            >
              <div className="card-body position-relative p-4">
                {/* Close Button */}
                <div className="position-absolute top-0 end-0 mt-3 me-3">
                  <i
                    className="ti ti-x"
                    style={{
                      background: "#6c757d",
                      color: "#fff",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      fontSize: "12px",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => setShowGuide(false)}
                  ></i>
                </div>

                {/* Heading */}
                <h5 className="fw-bold mb-3">Line of Business</h5>

                {/* Paragraph */}
                {/* <p className="mb-3 text-muted" style={{ lineHeight: "1.7" }}>
                  This allows you to more easily manage your Sub-Sub Business
                  Unit (SBU) and create a bifurcation that can be viewed easily.
                  For example an IT company may want to link SBUs with further
                  partitions in departments such as Support Function,
                  Operations, Sales or Technology. Additionally you may want to
                  insert locations Delhi, Mumbai, Chennai to add another filter
                  to the data.
                </p> */}
                <ul
                  className="mb-0 ps-3 text-dark"
                  style={{ lineHeight: "1.8", listStyleType: "disc" }}
                >
                  <li>
                    Create and manage lines of business associated with
                    different programs. Each line of business helps organize
                    screening requirements based on departments, business
                    functions, or operational units.
                  </li>
                  <li>
                    For example, an organization can configure lines of business
                    such as Operations, Sales, Customer Support, or Technology
                    under a program.
                  </li>
                  <li>
                    Use the hierarchy configuration to organize business units
                    further by department, sub-department, or location, where
                    applicable.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Table  */}
        {lobListLoading ? (
          <SnipperLoader />
        ) : (
          <div className="card mt-3">
            <Datatable
              dataSource={filteredData}
              columns={columns}
              Selection={false}
              fixedHeader
              tableHeight={650}
              rowKey="department_id"
              pagination={{
                current: pagination.page,
                pageSize: pagination.limit,
                total,
              }}
              handleTableChange={handleTableChange}
            />
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default LOBList;
